import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'

/**
 * قطعُ الصندوق اللمسيّة — <b>ومقاسُها قرارٌ لا ذوق</b>.
 *
 * الكاشيرُ يعمل بإبهامٍ واحدٍ وعينُه على الزبون، والشاشةُ تُلمس آلافَ المرّات
 * في اليوم. فالحدُّ الأدنى هنا <b>٤٤ بكسلًا</b> في كلّ ما يُلمس — وهو المقاسُ
 * الذي يُخطئ دونه الإبهامُ فيُضاف صنفٌ لم يُقصد، ويُكتشف الخطأُ عند الدفع.
 *
 * ⚠ <b>ولا قاعدةَ عملٍ في هذا الملفّ</b>: أشكالٌ وأحجامٌ فقط.
 */

/** زرٌّ كبيرٌ يُلمس بالإبهام — والحدُّ ٥٦ بكسلًا لما يُضغط كثيرًا. */
export function TouchButton({
  children,
  onClick,
  tone = 'ghost',
  disabled,
  wide,
  type = 'button',
}: {
  children: ReactNode
  onClick?: () => void
  tone?: 'primary' | 'ghost' | 'danger' | 'ok'
  disabled?: boolean
  wide?: boolean
  type?: 'button' | 'submit'
}) {
  const tones = {
    primary: 'bg-brand text-white',
    ghost: 'border border-line bg-surface',
    danger: 'border border-danger/50 bg-danger/10',
    ok: 'border border-ok/50 bg-ok/10',
  }

  return (
    <button
      className={
        'inline-flex min-h-14 items-center justify-center gap-2 rounded-xl px-4 text-base font-semibold ' +
        `transition active:scale-[0.98] disabled:opacity-40 ${tones[tone]} ${wide ? 'w-full' : ''}`
      }
      type={type}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  )
}

/**
 * حقلُ البحث/المسح — <b>ويستعيد التركيزَ دائمًا</b>.
 *
 * ⚠ وماسحُ الباركود <b>لوحةُ مفاتيح</b>: يكتب الأرقامَ ثمّ <kbd>Enter</kbd>.
 *   فإن لم يكن الحقلُ مركَّزًا ضاع المسحُ في الصفحة، ويظنّ الكاشيرُ أنّ الماسحَ
 *   عطبان. ولهذا يُعاد التركيزُ بعد كلّ إضافة.
 */
export function ScanInput({
  value,
  onChange,
  onSubmit,
  onArrow,
  onEscape,
  placeholder = 'امسح باركودًا أو اكتب اسمًا…',
  focusKey,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  /** ↑/↓ للتنقّل بين نتائج البحث بلا فأرة. */
  onArrow?: (step: 1 | -1) => void
  onEscape?: () => void
  placeholder?: string
  /** يتغيّر ⇒ يُعاد التركيز. */
  focusKey?: unknown
  disabled?: boolean
}) {
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    input.current?.focus()
  }, [focusKey])

  return (
    <input
      ref={input}
      className={
        'min-h-14 w-full rounded-xl border-2 border-line bg-surface px-4 text-lg outline-none ' +
        'focus:border-brand focus:ring-4 focus:ring-brand/20 disabled:opacity-50'
      }
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      autoComplete="off"
      enterKeyHint="search"
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          onSubmit()
          return
        }

        // ⚠ **والسهمُ يُمنع من تحريك المؤشّر داخل النصّ**: بلا `preventDefault`
        //   ينتقل الاختيارُ ويقفز المؤشّرُ معًا، فيبدو الحقلُ مضطربًا.
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          if (onArrow) {
            event.preventDefault()
            onArrow(event.key === 'ArrowDown' ? 1 : -1)
          }

          return
        }

        if (event.key === 'Escape' && onEscape) {
          event.preventDefault()
          onEscape()
        }
      }}
    />
  )
}

/**
 * لوحةُ أرقامٍ لمسيّة — للمبالغ حين لا تكون هناك لوحةُ مفاتيح.
 *
 * <b>والفاصلةُ نقطةٌ واحدةٌ لا أكثر</b>: الدينارُ ثلاثُ خانات، و«١٢..٥» ليست
 * رقمًا.
 */
export function Keypad({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const press = (key: string) => {
    if (key === '⌫') {
      onChange(value.slice(0, -1))
      return
    }

    if (key === '.' && value.includes('.')) {
      return
    }

    onChange(value + key)
  }

  return (
    <div className="grid grid-cols-3 gap-2">
      {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '⌫'].map((key) => (
        <button
          key={key}
          className="tabular min-h-14 rounded-xl border border-line bg-surface text-xl font-semibold active:scale-[0.98]"
          type="button"
          dir="ltr"
          onClick={() => press(key)}
        >
          {key}
        </button>
      ))}
    </div>
  )
}

/** ورقةٌ تعلو الشاشة — للدفع والمرتجع والإغلاق. */
export function Sheet({
  title,
  children,
  onClose,
}: {
  title: ReactNode
  children: ReactNode
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-surface">
      <header className="flex min-h-16 items-center justify-between gap-3 border-b border-line px-4">
        <h2 className="text-lg font-bold">{title}</h2>

        <button
          className="min-h-11 min-w-11 rounded-lg border border-line px-3 text-lg"
          type="button"
          aria-label="إغلاق"
          onClick={onClose}
        >
          ✕
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-4">{children}</div>
    </div>
  )
}

/** رقمٌ كبيرٌ بعنوانه — لما يُقرأ من بعيد: المجموعُ والفكّة. */
export function Figure({ label, value, tone }: { label: string; value: ReactNode; tone?: 'ok' | 'warn' }) {
  const tones = { ok: 'text-ok', warn: 'text-warn' }

  return (
    <div className="rounded-xl border border-line bg-surface-raised p-3 text-center">
      <div className="text-xs text-ink-soft">{label}</div>
      <div className={`tabular mt-1 text-2xl font-bold ${tone ? tones[tone] : ''}`} dir="ltr">
        {value}
      </div>
    </div>
  )
}
